/**
 * Cesium Configuration for OFFLINE MODE
 *
 * This configuration sets up Cesium to work without Cesium Ion services.
 * All assets are loaded locally for offline capability.
 */

import * as Cesium from 'cesium';

// Configure Cesium base URL for static assets
// Note: vite-plugin-cesium handles this automatically, but we keep for explicit control
const cesiumBaseUrl = import.meta.env.VITE_CESIUM_BASE_URL || '/cesium/';
// Comment out manual setting - let vite-plugin-cesium handle it
// window.CESIUM_BASE_URL = cesiumBaseUrl;

// IMPORTANT: Disable Cesium Ion for offline mode
// We don't use Ion's cloud services - everything runs locally
// Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1ZTAwNmFhMi0zYTRmLTQyOWQtYjViOS0zYjViY2NmMTc2MGUiLCJpZCI6MzM3Nzc2LCJpYXQiOjE3NTY4NzMyNzN9.gbAY0iPShi_nDciT9JqxEV1hIRtXhcvykkh6o77QG6E';

// Terrain Configuration
// Set VITE_TERRAIN_URL in .env to enable offline terrain
// After converting .tif files, place terrain tiles in public/terrain/
const terrainUrl = import.meta.env.VITE_TERRAIN_URL || '/terrain/';
const enableTerrain = import.meta.env.VITE_ENABLE_TERRAIN === 'true';

/**
 * Create terrain provider based on configuration
 * Returns CesiumTerrainProvider if terrain is enabled and available,
 * otherwise returns EllipsoidTerrainProvider (flat earth)
 */
export async function createTerrainProvider(): Promise<Cesium.TerrainProvider> {
  if (enableTerrain) {
    try {
      // Create offline terrain provider
      // Terrain tiles should be in public/terrain/ after conversion
      return await Cesium.CesiumTerrainProvider.fromUrl(terrainUrl, {
        requestVertexNormals: true, // For better lighting
        requestWaterMask: false, // Disable water effects for offline
      });
    } catch (error) {
      console.warn(
        '[Cesium] Failed to load terrain, using flat terrain:',
        error
      );
      return new Cesium.EllipsoidTerrainProvider();
    }
  }

  // Default: flat terrain (ellipsoid)
  return new Cesium.EllipsoidTerrainProvider();
}

// Default camera position (Indonesia - approximate KOPASGAT area)
export const DEFAULT_CAMERA_POSITION = {
  longitude: 106.8456, // Jakarta longitude
  latitude: -6.2088, // Jakarta latitude
  height: 150000, // 150km altitude for initial view
  heading: 0,
  pitch: -90, // Looking straight down
  roll: 0,
};

// Cesium viewer default options for offline usage
export const CESIUM_VIEWER_OPTIONS: Cesium.Viewer.ConstructorOptions = {
  // Terrain: Start with flat terrain, will be updated async if enabled

  // Base layer: Using default imagery provider
  // Will be replaced with TNI AL tiles in later phase
  baseLayerPicker: false,
  geocoder: false,
  homeButton: false,
  sceneModePicker: true,
  navigationHelpButton: false,
  animation: false,
  timeline: false,
  fullscreenButton: false,
  vrButton: false,

  // Performance settings
  requestRenderMode: false,
  maximumRenderTimeChange: Infinity,

  // Scene settings
  shadows: false,
  scene3DOnly: false,
};

// Export Cesium for use in components
export { Cesium };

// Log configuration (development only)
if (import.meta.env.VITE_DEV_MODE === 'true') {
  console.log('[Cesium] Configured in OFFLINE mode');
  console.log('[Cesium] Base URL:', cesiumBaseUrl);
  console.log('[Cesium] Ion disabled - using local assets only');
  console.log(
    '[Cesium] Terrain:',
    enableTerrain ? `Enabled (${terrainUrl})` : 'Disabled (flat terrain)'
  );
}
